import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import './index.scss';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import './i18n';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import { AUTH_KEY, AUTH_REFRESH_KEY, BASE_API_URL, EXCLUDED_URLS } from './constants/constants';
import { store } from './store/store';

if (process.env.REACT_APP_ENV === 'prod') {
  console.log = () => {};
}

const isExcludedAuthUrl = (url?: string) => {
  if (!url) return false;
  return EXCLUDED_URLS.some((excludedUrl) => url.indexOf(excludedUrl) !== -1);
};

axios.interceptors.request.use(function (config) {
  // Check if the request URL is in the excludedEndpoints array
  if (!isExcludedAuthUrl(config.url)) {
    config.headers.Authorization = `JWT ${localStorage.getItem(AUTH_KEY)}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Avoid refresh loops for auth endpoints and already retried requests.
    if (
      error?.response?.status === 401 &&
      !originalRequest._retry &&
      !isExcludedAuthUrl(originalRequest.url)
    ) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem(AUTH_REFRESH_KEY);

      if (!refreshToken) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axios.post(`${BASE_API_URL}/api/token/refresh/`, {
          refresh: refreshToken,
        });

        const accessToken = refreshResponse.data?.access;
        const rotatedRefreshToken = refreshResponse.data?.refresh;

        if (!accessToken) {
          throw error;
        }

        localStorage.setItem(AUTH_KEY, accessToken);
        if (rotatedRefreshToken) {
          localStorage.setItem(AUTH_REFRESH_KEY, rotatedRefreshToken);
        }

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `JWT ${accessToken}`;

        return axios(originalRequest);
      } catch (refreshError) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <Suspense fallback={<div>Loading...</div>}>
          <App />
        </Suspense>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root')
);

reportWebVitals();
